import { FC, useEffect, useState } from 'react'

import { formatDDELicenseProperties } from '@/components/forms/LicenseForm/utils'
import { ddeApi } from '@/service/ddeApi'
import { LicenseProperty } from '@/utils/api/licenseProperties'
import { getLicensePropertySport } from '@/utils/licenseUtils'

import { Autocomplete } from '../Autocomplete'
import {
  LEGACY_SPORT_GROUPNAME_LABEL,
  LICENSE_SPORTS_MAP,
  SELECT_SPORT_LICENSE_PROPERTY_LABEL,
  SPORT_LICENSE_PROPERTY_AUTOCOMPLETE_TEST_ID
} from './constants'

export type AutoCompleteOption = {
  label: string
  id: number | string
  groupName?: string
}

type Props = {
  sportLicenseProperty: LicenseProperty | null
  operatorId?: number
  onChange: (value: AutoCompleteOption | null) => void
  onClear: () => void
  editMode: boolean
}

const formatLicenseOption = (option: LicenseProperty) => ({
  id: option.value,
  label: option.label,
  groupName:
    LICENSE_SPORTS_MAP[getLicensePropertySport(option.label) || ''] || LEGACY_SPORT_GROUPNAME_LABEL
})

export const DDELicensePropertyAutocomplete: FC<Props> = ({
  sportLicenseProperty,
  operatorId,
  onChange,
  onClear,
  editMode
}) => {
  const [propertyOptions, setPropertyOptions] = useState<AutoCompleteOption[]>([])

  useEffect(() => {
    if (!operatorId) return

    const fetchOptions = async () => {
      const { data: license } = await ddeApi.endpoints.operators.getOperatorLicenses(operatorId)
      const { data: properties } = await ddeApi.endpoints.operators.getLicensesProperties()

      const formattedProperties = formatDDELicenseProperties(properties)
      const alreadyUsedProperties = license.map(({ property }) => property)

      if (editMode) {
        const properties = formattedProperties.map(formatLicenseOption).sort((a, b) => {
          const groupComparison = a.groupName.localeCompare(b.groupName)
          if (groupComparison !== 0) {
            return groupComparison
          }
          return a.label.localeCompare(b.label)
        })

        return setPropertyOptions(properties)
      }

      const notUsedProperties = formattedProperties
        .filter((p) => !alreadyUsedProperties.includes(p.value.toString()))
        .map(formatLicenseOption)
        .sort((a, b) => {
          const groupComparison = a.groupName.localeCompare(b.groupName)
          if (groupComparison !== 0) {
            return groupComparison
          }
          return a.label.localeCompare(b.label)
        })

      setPropertyOptions(notUsedProperties)
    }

    fetchOptions()
  }, [operatorId])

  return (
    <Autocomplete
      disabled={editMode}
      options={propertyOptions}
      value={
        sportLicenseProperty
          ? { label: sportLicenseProperty.label, id: sportLicenseProperty.value }
          : null
      }
      label={SELECT_SPORT_LICENSE_PROPERTY_LABEL}
      onClear={onClear}
      testId={SPORT_LICENSE_PROPERTY_AUTOCOMPLETE_TEST_ID}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => option.label}
      groupBy={(option) => option.groupName?.toUpperCase() || LEGACY_SPORT_GROUPNAME_LABEL}
      filterOptions={(options, state) =>
        options.filter((option) =>
          option.label.toLowerCase().includes(state.inputValue.toLowerCase())
        )
      }
      onChange={onChange}
    />
  )
}
